import React, { useEffect, useState } from "react";
import { ButtonOnlyTitle } from "../../Buttons/ButtonOnlyTitle/Index";
import { useEditOnboarding } from "../../../hooks/useEditOnboarding";
import axios from "../../../services/axios";
import InventoryTable from "./table";
import { IAreas } from "../../../@types/API/IArea";
import { FormProvider, useForm } from "react-hook-form";
import { Modal } from "../../Modal/Default/Index";
import { InventoryTaskStop } from "../../Modal/InventoryStopTasks";
import { format } from "date-fns";

interface FormValues {
  [batchIndex: string]: {
    [categoryId: string]: number;
  }
}

export const Inventory: React.FC = () => {
  const { setOptionForm, farmIdSelected, cardInfo, idSelected, } = useEditOnboarding();
  const [areaIds, setAreaIds] = useState<number[]>();
  const [inventory, setInventory] = useState<IBatchCategoryContent[]>();
  const [currentInventory, setCurrentInventory] = useState<IBatchCategoryContent[]>([]);
  const [completeTaskModal, setCompleteTaskModal] = useState(false);
  const [error, setError] = useState(false);
  const methods = useForm()
  const [idCategoryMap] = useState(new Map<string, any>());

  useEffect(() => {
    if (inventory) {
      methods.setValue('batches',
        inventory ?
          inventory.reduce((acc, batch) => {
            acc[batch.batch_id] = {};
            batch.categories.forEach((cat) => {
              acc[batch.batch_id][cat.id] = cat.quantity;
            });
            return acc;
          }, {} as FormValues)
          : undefined
      )
    }
  }, [inventory]);

  useEffect(() => {
    if (currentInventory) {
      methods.setValue('inventory',
        currentInventory ?
          currentInventory.reduce((acc, batch) => {
            acc[batch.batch_id] = {};
            batch.categories.forEach((cat) => {
              acc[batch.batch_id][cat.id] = cat.quantity;
            });
            return acc;
          }, {} as FormValues)
          : undefined
      )
    }
  }, [currentInventory]);

  useEffect(() => {
    axios
      .get(`/areas`, { params: { farm_id: farmIdSelected } })
      .then(({ data }) => {
        const areas: IAreas = data
        setAreaIds(areas.map(area => Number(area.id)))
      });
  }, []);

  useEffect(() => {
    const dateNow = format(new Date(), 'yyyy-MM-dd HH:mm:ss')

    if (farmIdSelected && areaIds && areaIds.length > 0) {
      axios
        .get(`/batches/categories?farmId=${farmIdSelected}&areaIds=${cardInfo.area_id}&size=${999}`)
        .then((response) => {
          const batchInventory = [...(response.data.content as IBatchCategoryContent[])]
            .sort((a, b) => a.initials.localeCompare(b.initials));

          batchInventory.map((batch: any) => {
            batch.categories = batch.categories.map((cat: any) => {
              const id = `${batch.batch_id}.${cat.id}`;
              if (!idCategoryMap.has(id)) {
                idCategoryMap.set(id, cat.id);
              }
              return cat;
            });
            return batch;
          });

          if (cardInfo.state === "COMPLETED") {
            axios.get(`/batch-category-history?taskId=${cardInfo.id}`)
              .then((response) => {
                const responseHistory = response.data as IInventoryFix[];

                const adjustmentsMap = new Map<number, number>();
                adjustmentsMap.clear();

                axios.get(`/area-inventory-adjustment-history?taskId=${cardInfo.id}`)
                  .then((response) => {
                    const responseAdjustments = response.data as IAdjustmentsInventoryHistory[];

                    responseAdjustments.forEach(item => {
                      adjustmentsMap.set(item.category_id, item.balance);
                    })
                  })

                const historyMap = new Map<string, IInventoryFix>();
                historyMap.clear();

                responseHistory.forEach(item => {
                  historyMap.set(`${item.batch_id}.${item.category_id}`, item);
                })

                const historyInventory = batchInventory.map((batch: IBatchCategoryContent) => {
                  return {
                    ...batch,
                    categories: Array.from(historyMap.values())
                      .filter(value => value.batch_id === batch.batch_id)
                      .map(value => ({
                        id: value.category_id,
                        quantity: value.quantity,
                        age_description: "",
                        inventory_adjustment: adjustmentsMap.get(value.category_id) || 0,
                        month_reference: 0,
                        name: "",
                        unit: ""
                      } as unknown as Category))
                  };
                });

                const allCategoriesEmpty = historyInventory.every(batch => batch.categories.length === 0);

                if (allCategoriesEmpty) {
                  axios.get(`/balance?farmId=${farmIdSelected}&areaIds=${cardInfo.area_id}&startDate=${dateNow}&endDate=${dateNow}`)
                    .then((balanceResponse) => {
                      const inventoryData = balanceResponse.data;
                      const updatedBatchInventory = batchInventory.map((batch: any) => {
                        const categories = batch.categories.map((cat: any) => {
                          const inventoryItem = inventoryData.find((item: any) => item.id == cat.id);
                          return {
                            ...cat,
                            inventory_adjustment: inventoryItem ? inventoryItem.inventory_adjustment : 0
                          };
                        });
                        return { ...batch, categories };
                      });
                      setInventory(updatedBatchInventory);
                    })
                    .catch((error) => {
                      setInventory(batchInventory);
                    });
                } else {
                  setInventory(historyInventory);
                }
              })
              .catch((error) => {
                setInventory(batchInventory);
              });
          } else {
            axios.get(`/balance?farmId=${farmIdSelected}&areaIds=${cardInfo.area_id}&startDate=${dateNow}&endDate=${dateNow}`)
              .then((balanceResponse) => {
                const inventoryData = balanceResponse.data;

                axios.get(`/adjustments?farmId=${farmIdSelected}&areaIds=${cardInfo.area_id}`)
                  .then(response => {
                    const adjustmentsMap = new Map();

                    response.data.forEach((category: any) => {
                      adjustmentsMap.set(category.category_id, category.balance);
                    });

                    const updatedBatchInventory = updateInventoryWithAdjustments(
                      batchInventory,
                      inventoryData,
                      adjustmentsMap
                    );
                    setInventory(updatedBatchInventory);
                  })
                  .catch(() => {
                    setInventory(batchInventory);
                  });
              })
              .catch(() => {
                setInventory(batchInventory);
              });
          }
        });
    }
  }, [areaIds]);

  const updateInventoryWithAdjustments = (batchInventory: IBatchCategoryContent[], inventoryData: IBalance[], adjustmentsMap: Map<number | undefined, any>) => {
    return batchInventory.map((batch) => {
      const categories = batch.categories.map((cat) => {
        const inventoryItem = inventoryData.find((item) => item.id === cat.id);
        return {
          ...cat,
          inventory_adjustment: adjustmentsMap.has(inventoryItem?.id)
            ? adjustmentsMap.get(inventoryItem?.id)
            : (inventoryItem?.inventory_adjustment ?? 0),
        };
      });
      return { ...batch, categories };
    });
  };

  const updateInventoryWithoutAdjustments = (batchInventory: IBatchCategoryContent[], inventoryData: IBalance[]) => {
    return batchInventory.map((batch) => {
      const categories = batch.categories.map((cat) => {
        const inventoryItem = inventoryData.find((item) => item.id === cat.id);
        return {
          ...cat,
          inventory_adjustment: inventoryItem?.inventory_adjustment ?? 0,
        };
      });
      return { ...batch, categories };
    });
  };

  useEffect(() => {
    console.log(cardInfo)
    if (inventory && idSelected && cardInfo.date) {
      idCategoryMap.clear();
      axios.get(`/tasks/batch-inventory-adjustment/${idSelected}`)
        .then((response) => {
          const responseAdjustment = response.data as IInventoryFix[];
          const adjustmentMap = new Map<string, IInventoryFix>();
          responseAdjustment.forEach(item => {
            adjustmentMap.set(`${item.batch_id}.${item.category_id}`, item);
            idCategoryMap.set(`${item.batch_id}.${item.category_id}`, item.id);
          });

          const updatedInventory = inventory.map(batch => {
            return {
              ...batch,
              categories: Array.from(adjustmentMap.values())
                .filter(value => value.batch_id === batch.batch_id)
                .map(value => ({
                  id: value.category_id,
                  quantity: value.quantity,
                  age_description: "",
                  inventory_adjustment: 0,
                  month_reference: 0,
                  name: "",
                  unit: ""
                } as unknown as Category))
            };
          });

          if (cardInfo.state === "COMPLETED") {
            setCurrentInventory(inventory)
          } else if (cardInfo.state === "DRAFT") {
            setCurrentInventory(updatedInventory)
          } else {
            setCurrentInventory(updatedInventory)
          }
        })
        .catch(() => setCurrentInventory(inventory))
    } else if (inventory) {
      setCurrentInventory(inventory)
    }
  }, [inventory, idSelected]);

  const getInventoryArray = (data: any) =>
    Object.entries(data.inventory || {}).flatMap(([batchId, categories]) => {
      console.log(idCategoryMap)

      return Object.entries(categories as Record<string, number>).map(([categoryId, quantity], index) => ({
        batch_id: Number(batchId),
        category_id: Number(categoryId),
        quantity,
        id: idCategoryMap.get(`${batchId}.${categoryId}`) || index,
      }))
    });

  const postInventory = async (
    inventoryArray: any[],
    state: "DRAFT" | "PENDING"
  ) => {
    if (!areaIds || areaIds.length === 0) return;
    const body = {
      type: "BATCH_INVENTORY_ADJUSTMENT",
      state,
      changes: inventoryArray,
      draft_task_id: idSelected,
      area_id: cardInfo.area_id
    };

    axios.post("/tasks", body)
      .then(() => setError(false))
      .catch(err => {
        console.error(err)
        setError(true)
      }).finally(() => {
        if (state == "PENDING")
          setCompleteTaskModal(true);
        else setOptionForm(undefined)
      })
  };

  function handleCompleteInventory(data: any) {
    if (!areaIds || areaIds.length === 0) return;
    const inventoryArray = getInventoryArray(data);

    postInventory(inventoryArray, "PENDING");
  }

  function handleDraftInventory(data: any) {
    if (!areaIds || areaIds.length === 0) return;
    const inventoryArray = getInventoryArray(data);

    postInventory(inventoryArray, "DRAFT");
  }

  return (
    <>
      <FormProvider {...methods}>
        <form>
          <div
            style={{
              overflowY: "scroll",
              height: "calc(100vh - 340px)",
            }}
            id="formInventory"
          >
            <>
              {inventory && (
                <>
                  <h2>Lotes atuais</h2>
                  <InventoryTable
                    dataTable={inventory}
                    onlyRead
                    typeTable={'batches'}
                    methods={methods as any}
                  />
                </>
              )}
              <br />
              {currentInventory && inventory && (
                <>
                  <h2>Correção de inventário</h2>
                  <InventoryTable
                    dataTable={currentInventory}
                    referenceTable={inventory}
                    typeTable={'inventory'}
                    methods={methods as any}
                    onlyRead={cardInfo.state === "PENDING" || cardInfo.state === "COMPLETED"}
                    viewAdjustment
                  />
                </>
              )}
            </>
          </div>

          <div className="footer__EditOnboarding">
            <div className="footer__formEditOnboarding">
              <ButtonOnlyTitle title="Voltar" theme="light" onClick={() => setOptionForm(undefined)} />
              <ButtonOnlyTitle title="Salvar" theme="light" onClick={methods.handleSubmit(data => handleDraftInventory(data))} disabled={cardInfo.state === "PENDING" || cardInfo.state === "COMPLETED"} />
              <ButtonOnlyTitle title="Concluir" theme="info" disabled={(cardInfo.state === "PENDING" || cardInfo.state === "COMPLETED") || completeTaskModal} onClick={methods.handleSubmit(data => handleCompleteInventory(data))} />
            </div>
          </div>
        </form>
      </FormProvider>

      <Modal
        visible={completeTaskModal}
        contentStyle={{
          padding: "60px",
          width: "433px",
          height: "auto",
          borderRadius: 25,
          position: "relative",
          display: "flex",
          flexDirection: "column",
          gap: 30
        }}
      >
        <span>
          {error ? "Ocorreu um erro na ação" : "É necessário que o capataz realize a tarefa em campo."}
        </span>
        <footer className="buttons-container">
          <ButtonOnlyTitle
            title="Concluir"
            theme="info"
            onClick={() => {
              setCompleteTaskModal(false)
              if (!error)
                setOptionForm(undefined)
            }}
          />
        </footer>
      </Modal>

      <InventoryTaskStop />
    </>
  );
};
